<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { useHrApi } from '../composables/useHrApi'
import EmployeePickerModal from '../components/EmployeePickerModal.vue'
import { dateRange, monthValue, toDateInput } from '../utils/format'
import type { AttendanceRecord, Employee, Option } from '../types/hr'

const { t } = useI18n()
const auth = useAuthStore()
const route = useRoute()
const { listAllAttendance, createAttendance, deleteAttendance, getEmployee, listWorkplaceOptions } = useHrApi()

const permissions = computed(() => auth.user?.permissions ?? [])
const canManage = computed(() => permissions.value.includes('attendance.manage'))

const today = toDateInput(new Date())

const records = ref<AttendanceRecord[]>([])
const todayRecords = ref<AttendanceRecord[]>([])
const departments = ref<Option[]>([])
const month = ref(monthValue(today))
const departmentId = ref<string | number>('')
const loading = ref(true)
const error = ref('')
const actionError = ref('')
const actionMessage = ref('')
const submitting = ref(false)

const showForm = ref(false)
const showEmployeePicker = ref(false)
const selectedEmployee = ref<Employee | null>(null)

const form = reactive({
  employee_id: null as number | null,
  from: today,
  to: today,
  notes: '',
})

function rangeFor(value: string): { from: string; to: string } {
  const [year, monthPart] = value.split('-').map(Number)
  if (!year || !monthPart) return { from: today, to: today }
  const lastDay = new Date(year, monthPart, 0).getDate()
  return { from: `${value}-01`, to: `${value}-${String(lastDay).padStart(2, '0')}` }
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const range = rangeFor(month.value)
    records.value = await listAllAttendance({
      status: 'leave',
      date_from: range.from,
      date_to: range.to,
      department_agent_id: departmentId.value || undefined,
    })
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function loadToday() {
  try {
    todayRecords.value = await listAllAttendance({ status: 'leave', date_from: today, date_to: today })
  } catch {
    todayRecords.value = []
  }
}

async function loadDepartments() {
  try {
    departments.value = await listWorkplaceOptions()
  } catch {
    departments.value = []
  }
}

function refresh() {
  void load()
  void loadToday()
}

onMounted(() => {
  void loadDepartments()
  refresh()
  void applyQueryPrefill()
})

watch([month, departmentId], () => {
  void load()
})

const summary = computed(() => {
  const perEmployee = new Map<number, { name: string; department: string; days: number; last: string }>()
  for (const record of records.value) {
    const entry = perEmployee.get(record.employee_id) ?? {
      name: record.full_name,
      department: record.department_name || '—',
      days: 0,
      last: record.attendance_date,
    }
    entry.days += 1
    if (record.attendance_date > entry.last) entry.last = record.attendance_date
    perEmployee.set(record.employee_id, entry)
  }
  return [...perEmployee.entries()]
    .map(([id, entry]) => ({ id, ...entry }))
    .sort((a, b) => b.days - a.days)
})

const register = computed(() =>
  [...records.value].sort((a, b) => b.attendance_date.localeCompare(a.attendance_date)),
)

const averageDays = computed(() => {
  if (summary.value.length === 0) return 0
  const total = summary.value.reduce((sum, row) => sum + row.days, 0)
  return total / summary.value.length
})

function openForm() {
  actionError.value = ''
  actionMessage.value = ''
  selectedEmployee.value = null
  form.employee_id = null
  form.from = `${month.value}-01`
  form.to = rangeFor(month.value).to
  form.notes = ''
  showForm.value = true
}

/**
 * Deep link from the employee profile: opens the leave form for one day with
 * the employee already selected (`/leave?employee=42&action=new`).
 */
async function applyQueryPrefill() {
  const employeeId = Number(route.query.employee ?? '')
  if (!employeeId || route.query.action !== 'new' || !canManage.value) return
  openForm()
  form.from = today
  form.to = today
  try {
    const employee = await getEmployee(employeeId)
    selectedEmployee.value = employee
    form.employee_id = employee.id
  } catch {
    // Keep the form open without a preselected employee.
  }
}

function selectEmployee(employee: Employee) {
  selectedEmployee.value = employee
  form.employee_id = employee.id
  showEmployeePicker.value = false
}

async function submit() {
  actionError.value = ''
  actionMessage.value = ''
  if (!form.employee_id) {
    actionError.value = t('hr.leave.invalidEmployee')
    return
  }
  const dates = dateRange(form.from, form.to)
  if (dates.length === 0) {
    actionError.value = t('hr.leave.invalidRange')
    return
  }

  submitting.value = true
  let created = 0
  let skipped = 0
  try {
    for (const date of dates) {
      try {
        await createAttendance({
          employee_id: form.employee_id,
          attendance_date: date,
          status: 'leave',
          worked_minutes: 0,
          notes: form.notes.trim() || null,
        })
        created += 1
      } catch {
        skipped += 1
      }
    }

    if (created === 0) {
      actionError.value = t('hr.leave.noneCreated')
    } else if (skipped > 0) {
      actionMessage.value = t('hr.leave.createdPartial', { count: created, skipped })
    } else {
      actionMessage.value = t('hr.leave.created', { count: created })
    }
    showForm.value = false
    refresh()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

async function remove(record: AttendanceRecord) {
  actionError.value = ''
  actionMessage.value = ''
  if (!window.confirm(t('hr.leave.deleteConfirm'))) return
  try {
    await deleteAttendance(record.id)
    actionMessage.value = t('hr.leave.deleted')
    refresh()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  }
}

function employeeName(employee: Employee): string {
  const value = `${employee.last_name ?? ''} ${employee.first_name ?? ''}`.trim()
  return value || employee.employee_code
}
</script>

<template>
  <div class="page-heading">
    <h2>{{ t('hr.leave.title') }}</h2>
  </div>

  <div class="toolbar">
    <input v-model="month" class="date-input" type="month">
    <select v-model="departmentId" class="filter-select">
      <option value="">{{ t('hr.common.allDepartments') }}</option>
      <option v-for="department in departments" :key="department.id" :value="department.id">{{ department.name }}</option>
    </select>
    <span class="spacer" />
    <button v-if="canManage" class="btn-primary" type="button" @click="openForm">
      {{ t('hr.leave.newLeave') }}
    </button>
  </div>

  <div class="summary-row">
    <div class="summary-card accent">
      <span class="summary-value">{{ records.length }}</span>
      <span class="summary-label">{{ t('hr.leave.leaveDays') }}</span>
    </div>
    <div class="summary-card">
      <span class="summary-value">{{ summary.length }}</span>
      <span class="summary-label">{{ t('hr.leave.employeesOnLeave') }}</span>
    </div>
    <div class="summary-card">
      <span class="summary-value">{{ Math.round(averageDays * 10) / 10 }}</span>
      <span class="summary-label">{{ t('hr.leave.daysUsed') }}</span>
    </div>
    <div class="summary-card">
      <span class="summary-value">{{ todayRecords.length }}</span>
      <span class="summary-label">{{ t('hr.leave.onLeaveToday') }}</span>
    </div>
  </div>

  <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>
  <p v-if="actionError" class="error-inline">{{ actionError }}</p>

  <h3 class="section-title">{{ t('hr.leave.onLeaveToday') }}</h3>
  <div v-if="todayRecords.length === 0" class="panel">
    <span class="empty-inline">{{ t('hr.leave.noOnLeaveToday') }}</span>
  </div>
  <div v-else class="panel">
    <ul class="plain-list">
      <li v-for="record in todayRecords" :key="record.id">
        <strong>{{ record.full_name }}</strong>
        <span>{{ record.department_name || '—' }}<template v-if="record.notes"> · {{ record.notes }}</template></span>
      </li>
    </ul>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('hr.leave.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="refresh">{{ t('hr.common.retry') }}</button>
  </div>

  <template v-else>
    <h3 class="section-title">{{ t('hr.leave.summaryTitle') }}</h3>
    <div v-if="summary.length === 0" class="panel">
      <span class="empty-inline">{{ t('hr.leave.empty') }}</span>
    </div>
    <div v-else class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('hr.leave.employee') }}</th>
            <th>{{ t('hr.leave.department') }}</th>
            <th>{{ t('hr.leave.days') }}</th>
            <th>{{ t('hr.leave.lastLeave') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in summary" :key="row.id">
            <td class="cell-name">{{ row.name }}</td>
            <td>{{ row.department }}</td>
            <td class="cell-num">{{ row.days }}</td>
            <td class="cell-date">{{ formatDisplayDate(row.last) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <h3 class="section-title">{{ t('hr.leave.registerTitle') }}</h3>
    <div v-if="register.length === 0" class="panel">
      <span class="empty-inline">{{ t('hr.leave.empty') }}</span>
    </div>
    <div v-else class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('hr.leave.date') }}</th>
            <th>{{ t('hr.leave.employee') }}</th>
            <th>{{ t('hr.leave.department') }}</th>
            <th>{{ t('hr.leave.notes') }}</th>
            <th v-if="canManage" class="cell-actions">{{ t('hr.common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="record in register" :key="record.id">
            <td class="cell-date">{{ formatDisplayDate(record.attendance_date) }}</td>
            <td class="cell-name">
              {{ record.full_name }}
              <small class="cell-sub">{{ record.employee_code }}</small>
            </td>
            <td>{{ record.department_name || '—' }}</td>
            <td>{{ record.notes || '—' }}</td>
            <td v-if="canManage" class="cell-actions">
              <button class="link-action danger" type="button" @click="remove(record)">
                {{ t('hr.common.delete') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </template>

  <div v-if="showForm" class="modal-overlay" @click.self="showForm = false">
    <form class="modal" @submit.prevent="submit">
      <h3>{{ t('hr.leave.recordTitle') }}</h3>
      <p class="empty-inline">{{ t('hr.leave.recordHint') }}</p>
      <div class="form-grid tight">
        <div class="field field-wide">
          <label>{{ t('hr.leave.employee') }}</label>
          <button class="field-button" type="button" @click="showEmployeePicker = true">
            <span :class="{ placeholder: !selectedEmployee }">
              {{ selectedEmployee ? employeeName(selectedEmployee) : t('hr.leave.invalidEmployee') }}
            </span>
            <span aria-hidden="true">▾</span>
          </button>
        </div>
        <div class="field">
          <label for="leave-from">{{ t('hr.leave.from') }}</label>
          <input id="leave-from" v-model="form.from" type="date" required>
        </div>
        <div class="field">
          <label for="leave-to">{{ t('hr.leave.to') }}</label>
          <input id="leave-to" v-model="form.to" type="date" required>
        </div>
        <div class="field field-wide">
          <label for="leave-notes">{{ t('hr.leave.notes') }}</label>
          <textarea id="leave-notes" v-model="form.notes" />
        </div>
      </div>

      <p v-if="actionError" class="error-inline">{{ actionError }}</p>

      <div class="modal-actions">
        <button class="btn-secondary" type="button" @click="showForm = false">{{ t('hr.common.cancel') }}</button>
        <button class="btn-primary" type="submit" :disabled="submitting">
          {{ submitting ? t('hr.common.saving') : t('hr.leave.record') }}
        </button>
      </div>
    </form>
  </div>

  <EmployeePickerModal
    :open="showEmployeePicker"
    @close="showEmployeePicker = false"
    @select="selectEmployee"
  />
</template>

<style scoped>
.section-title { margin: 24px 0 10px; font-size: 14px; font-weight: 700; color: #2b2027; }
</style>
